import type { SyncAction, SyncActionType } from '@/services/syncQueue';

const TYPE_FALLBACK: Record<SyncActionType, string> = {
  create_expense: 'Add expense',
  update_expense: 'Update expense',
  delete_expense: 'Delete expense',
  create_booking: 'Add booking',
  update_booking: 'Update booking',
  delete_booking: 'Delete booking',
  update_trip: 'Update trip',
  create_itinerary_item: 'Add itinerary item',
  update_itinerary_item: 'Update itinerary item',
  delete_itinerary_item: 'Remove itinerary item',
  create_comment: 'Add comment',
  update_comment: 'Update comment',
  delete_comment: 'Delete comment',
  upload_document: 'Upload document',
  save_manual_document: 'Save document',
  update_maybe_item: 'Update maybe item',
  archive_maybe_item: 'Archive maybe item',
};

/**
 * Short, user-facing label for a queued sync action (Sync Status modal / debugging).
 */
export function describeSyncAction(action: SyncAction): string {
  const p = action.payload as Record<string, unknown>;

  switch (action.type) {
    case 'upload_document': {
      const name = p?.documentName || p?.fileName;
      return typeof name === 'string' && name.trim()
        ? `Upload: ${name.trim()}`
        : TYPE_FALLBACK.upload_document;
    }
    case 'save_manual_document': {
      const name = p?.name;
      return typeof name === 'string' && name.trim()
        ? `Document: ${name.trim()}`
        : TYPE_FALLBACK.save_manual_document;
    }
    case 'create_itinerary_item':
    case 'update_itinerary_item': {
      const item = p?.item as Record<string, unknown> | undefined;
      const title = item?.title;
      if (typeof title === 'string' && title.trim()) {
        return `${action.type === 'create_itinerary_item' ? 'Itinerary' : 'Itinerary update'}: ${title.trim().slice(0, 56)}`;
      }
      return TYPE_FALLBACK[action.type];
    }
    case 'create_expense': {
      const desc = p?.description;
      if (typeof desc === 'string' && desc.trim()) {
        return `Expense: ${desc.trim().slice(0, 56)}`;
      }
      return TYPE_FALLBACK.create_expense;
    }
    case 'update_expense':
    case 'delete_expense':
    case 'delete_itinerary_item':
    case 'create_booking':
    case 'update_booking':
    case 'delete_booking':
    case 'update_trip':
    case 'create_comment':
    case 'update_comment':
    case 'delete_comment':
    case 'update_maybe_item':
    case 'archive_maybe_item':
      return TYPE_FALLBACK[action.type];
    default:
      return (action.type as string).replace(/_/g, ' ');
  }
}
